import { useRef } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { Date as EventDate } from "../types";
import DateSelect from "@/components/DateSelect";
import RadioItem from "@/components/Radio/RadioItem";
import { useHasRoles } from "@/hooks/useCapabilities";
import { ROLE } from "@/providers/AuthorizationProvider/types";
import { getPATRegistrationCloseMaxDate, isPATRegistrationCloseEditable } from "../utils";

const WAITLIST_TYPES = [
  {
    value: "disabled",
    label: "No",
    subtext: "Registration disables when event is full"
  },
  {
    value: "notification",
    label: "Yes",
    subtext: "Everyone on the waitlist will receive an email each time a spot opens"
  }
];

type RegistrationFieldName = "registrationOpenDate" | "registrationCloseDate";

function getRegCloseSupportingText(
  isPAT: boolean,
  isRecruitment: boolean,
  isRegistrationCloseEditable?: boolean
): string {
  if (isRegistrationCloseEditable) {
    return "To accommodate an event reschedule, the registration close date may be updated to enable additional registrations.\nNote: the new registration close date must remain a minimum of 7 days prior to the new event date.";
  }
  if (isPAT) return "PAT events must be closed one week before the start date";
  if (isRecruitment) return "Recruitment events use automatic registration dates";
  return "";
}

type RegistrationDetailsStepProps = {
  hasMaxAttendance: boolean;
  isEditing?: boolean;
  hasWaitlistEntries?: boolean;
};

export default function RegistrationDetailsStep({
  hasMaxAttendance,
  isEditing,
  hasWaitlistEntries = false
}: RegistrationDetailsStepProps) {
  const {
    control,
    watch,
    setValue,
    trigger,
    formState: { errors, defaultValues }
  } = useFormContext();

  const eventType = watch("eventType");
  const sessions = watch("sessions");
  const registrationCloseDate = watch("registrationCloseDate");
  const isPAT = eventType.name === "PAT";
  const isRecruitment = eventType.name === "Recruitment";
  const canEditPatRegistrationClose = useHasRoles([ROLE.MEMBERSHIP_SERVICES_ADMIN]);

  // PAT close is locked unless Membership Services Admin is editing a reschedule
  // (session changing now, or a prior reschedule left close out of sync).
  const currentSessionDate = sessions?.[0]?.date?.startDate;
  const originalSessionDate = (defaultValues as { sessions?: { date?: { startDate?: string } }[] })?.sessions?.[0]?.date
    ?.startDate;
  const isRegistrationCloseEditable =
    isPAT &&
    canEditPatRegistrationClose &&
    isPATRegistrationCloseEditable({
      isEditing,
      currentSessionDate,
      originalSessionDate,
      sessions,
      registrationCloseDate: registrationCloseDate?.startDate
    });

  const patMaxDate = isRegistrationCloseEditable ? getPATRegistrationCloseMaxDate(sessions) : undefined;

  const prevValues = useRef<{
    registrationOpenDate: EventDate;
    registrationCloseDate: EventDate;
  }>({ registrationOpenDate: { endDate: "", startDate: "" }, registrationCloseDate: { startDate: "", endDate: "" } });

  const handleFocus = (field: RegistrationFieldName) => {
    prevValues.current[field] = watch(field);
  };

  const handleBlur = (field: RegistrationFieldName) => {
    const currentValue = watch(field);

    if (!currentValue?.startDate?.trim()) {
      setValue(field, prevValues.current[field]);
    }

    trigger();
  };

  const handleChange = (field: RegistrationFieldName, value: any) => {
    setValue(field, value);
  };

  return (
    <div className="flex flex-col gap-6 mb-0">
      <p className="text-title-large mb-2">Enter registration options for this event.</p>
      <Controller
        name="registrationOpenDate"
        control={control}
        render={({ field }) => (
          <DateSelect
            label="Registration Open *"
            value={field.value}
            useSingleDate
            popoverDirection="down"
            minDate={new Date()}
            onChange={(value) => handleChange("registrationOpenDate", value)}
            onFocus={() => handleFocus("registrationOpenDate")}
            onBlur={() => handleBlur("registrationOpenDate")}
          />
        )}
      />
      <Controller
        name="registrationCloseDate"
        control={control}
        render={({ field }) => (
          <DateSelect
            label="Registration Close *"
            value={field.value}
            useSingleDate
            popoverDirection="down"
            disabled={(isPAT && !isRegistrationCloseEditable) || isRecruitment}
            minDate={new Date()}
            maxDate={patMaxDate}
            supportingText={getRegCloseSupportingText(isPAT, isRecruitment, isRegistrationCloseEditable)}
            onChange={(value) => handleChange("registrationCloseDate", value)}
            onFocus={() => handleFocus("registrationCloseDate")}
            onBlur={() => handleBlur("registrationCloseDate")}
            error={!!errors.registrationCloseDate}
            errorMessage={errors.registrationCloseDate?.message as string}
          />
        )}
      />
      {hasMaxAttendance && (
        <div className="mt-6">
          <p className="text-title-medium mb-2">Enable Waitlist</p>

          {isEditing && hasWaitlistEntries && (
            <p className="text-body-small text-sys-brand-on-surface-variant mb-2">
              The waitlist cannot be disabled while there are people on it. Remove all entries from the waitlist before
              changing this setting.
            </p>
          )}

          <Controller
            control={control}
            name="waitlistType"
            data-testid="waitlist-type-radio-group"
            render={({ field }) => (
              <div className="flex flex-col">
                {WAITLIST_TYPES.map((option, i) => (
                  <div className="py-2 cursor-pointer" key={i}>
                    <div className="mb-3">
                      <RadioItem
                        name="waitlistType"
                        onChange={() => {
                          field.onChange(option.value);
                        }}
                        checked={field.value === option.value || field.value === "default"}
                        headline={option.label}
                        supportingText={option.subtext}
                        disabled={isEditing && hasWaitlistEntries}
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
          />
        </div>
      )}
    </div>
  );
}
